import { useCallback } from "react"
import { useLevantamentoQuantitativoApi } from "../../../infra/geo-veles"
import { useAppDispatch, useAppSelector } from "../../../presentation/hooks/store-hooks"
import { useUIService } from "../../ui"
import { resetCreateLevantamentoFormState, resetEditLevantamentoFormState, resetExtensaoArquivoSelecionadoState, resetLevantamentoSelecionadoState, setCreateLevantamentoFormColunasState, setCreateLevantamentoFormErrorsState, setCreateLevantamentoFormNomeState, setCreateLevantamentoFormOpenState, setCreateLevantamentoFormTouchedState, setEditLevantamentoFormColunasState, setEditLevantamentoFormErrorsState, setEditLevantamentoFormIdVelesGeoLevantamentoQuantitativoState, setEditLevantamentoFormNomeState, setEditLevantamentoFormOpenState, setEditLevantamentoFormTouchedState, setExtensaoArquivoSelecionadoState, setLevantamentosDoProjetoState, setLevantamentoSelecionadoState, setOpenDeleteLevantamentoState, setOpenDownloadLevantamentoState, validateCreateLevantamentoQuantitativoForm, validateEditLevantamentoQuantitativoForm } from "../../../application/states/geo-veles"
import { IExtensaoArquivo, ILevantamentoQuantitativo, IPessoaFisica } from "../../../application/models"
import { columnValueToLabel, convertToCSV, xlsxGenerator } from "../../../utils"

export const useLevantamentoQuantitativoService = () => {
    // Dispatch
    const dispatch = useAppDispatch()
    // State
    const {
        createLevantamentoForm,
        editLevantamentoForm,
        extensaoArquivoSelecionada,
        levantamentoSelecionado
    } = useAppSelector((state) => state.levantamentoQuantitativo)
    const {
        user: { currentUser },
        projetos: { projetoAberto }
    } = useAppSelector((state) => state)
    // Api
    const {
        createLevantamentoApi,
        deleteLevantamentoApi,
        findLevantamentosByIdProjetoApi,
        updateLevantamentoApi,
        processarLevantamentoApi
    } = useLevantamentoQuantitativoApi()
    // Service
    const { setAlert, setLoading } = useUIService()
    // Actions
    const createLevantamento = useCallback(async () => {
        if (currentUser) {
            if (projetoAberto) {
                const loadingId = Math.random().toString()
                try {
                    const errors = validateCreateLevantamentoQuantitativoForm(createLevantamentoForm.fields)

                    if (errors.length > 0) {
                        dispatch(setCreateLevantamentoFormErrorsState(errors))
                        dispatch(setCreateLevantamentoFormTouchedState(true))
                    } else {
                        setLoading({
                            id: loadingId,
                            loading: true,
                            message: 'Criando...'
                        })

                        await createLevantamentoApi({
                            Colunas: createLevantamentoForm.fields.Colunas,
                            IdVelesGeoProjeto: projetoAberto.IdVelesGeoProjeto,
                            Nome: createLevantamentoForm.fields.Nome
                        })

                        dispatch(setCreateLevantamentoFormOpenState(false))
                        dispatch(resetCreateLevantamentoFormState())

                        const levantamentosList = await findLevantamentosByIdProjetoApi({
                            IdVelesGeoProjeto: projetoAberto.IdVelesGeoProjeto
                        })

                        dispatch(setLevantamentosDoProjetoState(levantamentosList))
                    }
                } catch (error: any) {
                    setAlert({
                        message: error.response.data.error || error.response.data.message,
                        type: 'error'
                    })
                } finally {
                    setLoading({
                        id: loadingId,
                        loading: false
                    })
                }
            }
        }
    }, [createLevantamentoApi, createLevantamentoForm.fields, currentUser, dispatch, findLevantamentosByIdProjetoApi, projetoAberto, setAlert, setLoading])

    const updateLevantamento = useCallback(async () => {
        if (currentUser) {
            if (projetoAberto) {
                const loadingId = Math.random().toString()
                try {
                    const errors = validateEditLevantamentoQuantitativoForm(editLevantamentoForm.fields)

                    if (errors.length > 0) {
                        dispatch(setEditLevantamentoFormErrorsState(errors))
                        dispatch(setEditLevantamentoFormTouchedState(true))
                    } else {
                        setLoading({
                            id: loadingId,
                            loading: true,
                            message: 'Atualizando...'
                        })

                        await updateLevantamentoApi({
                            Colunas: editLevantamentoForm.fields.Colunas,
                            IdVelesGeoLevantamentoQuantitativo: editLevantamentoForm.fields.IdVelesGeoLevantamentoQuantitativo,
                            Nome: editLevantamentoForm.fields.Nome
                        })

                        const levantamentosList = await findLevantamentosByIdProjetoApi({
                            IdVelesGeoProjeto: projetoAberto.IdVelesGeoProjeto
                        })

                        dispatch(setLevantamentosDoProjetoState(levantamentosList))
                        dispatch(setEditLevantamentoFormOpenState(false))
                        dispatch(resetEditLevantamentoFormState())
                    }
                } catch (error: any) {
                    setAlert({
                        message: error.response.data.error || error.response.data.message,
                        type: 'error'
                    })
                } finally {
                    setLoading({
                        id: loadingId,
                        loading: false
                    })
                }
            }
        }
    }, [currentUser, dispatch, editLevantamentoForm.fields, findLevantamentosByIdProjetoApi, projetoAberto, setAlert, setLoading, updateLevantamentoApi])

    const deleteLevantamento = useCallback(async (IdVelesGeoLevantamentoQuantitativo: number) => {
        if (currentUser) {
            if (projetoAberto) {
                try {
                    await deleteLevantamentoApi({ IdVelesGeoLevantamentoQuantitativo })

                    const levantamentosList = await findLevantamentosByIdProjetoApi({
                        IdVelesGeoProjeto: projetoAberto.IdVelesGeoProjeto
                    })

                    dispatch(setLevantamentosDoProjetoState(levantamentosList))
                } catch (error: any) {
                    setAlert({
                        message: error.response.data.error || error.response.data.message,
                        type: 'error'
                    })
                }
            }
        }
    }, [currentUser, deleteLevantamentoApi, dispatch, findLevantamentosByIdProjetoApi, projetoAberto, setAlert])

    const findLevantamentosByIdProjeto = useCallback(async (IdVelesGeoProjeto: number) => {
        if (currentUser) {
            const loadingId = Math.random().toString()
            try {
                setLoading({
                    id: loadingId,
                    loading: true,
                    message: 'Buscando levantamentos deste projeto...'
                })

                const levantamentosList = await findLevantamentosByIdProjetoApi({ IdVelesGeoProjeto })

                dispatch(setLevantamentosDoProjetoState(levantamentosList))
            } catch (error: any) {
                setAlert({
                    message: error.response.data.error || error.response.data.message,
                    type: 'error'
                })
            } finally {
                setLoading({
                    id: loadingId,
                    loading: false
                })
            }
        }
    }, [currentUser, dispatch, findLevantamentosByIdProjetoApi, setAlert, setLoading])

    const setExtensaoArquivoSelecionado = useCallback((extensao: IExtensaoArquivo) => {
        dispatch(setExtensaoArquivoSelecionadoState(extensao))
    }, [dispatch])

    const resetExtensaoArquivoSelecionado = useCallback(() => {
        dispatch(resetExtensaoArquivoSelecionadoState())
    }, [dispatch])

    const setCreateLevantamentoFormNome = useCallback((Nome: string) => {
        dispatch(setCreateLevantamentoFormNomeState(Nome))
    }, [dispatch])

    const setCreateLevantamentoFormColunas = useCallback((Colunas: string[]) => {
        dispatch(setCreateLevantamentoFormColunasState(Colunas))
    }, [dispatch])

    const setCreateLevantamentoFormOpen = useCallback((open: boolean) => {
        dispatch(setCreateLevantamentoFormOpenState(open))
    }, [dispatch])

    const setEditLevantamentoFormIdVelesGeoLevantamentoQuantitativo = useCallback((IdVelesGeoLevantamentoQuantitativo: number) => {
        dispatch(setEditLevantamentoFormIdVelesGeoLevantamentoQuantitativoState(IdVelesGeoLevantamentoQuantitativo))
    }, [dispatch])

    const setEditLevantamentoFormNome = useCallback((Nome: string) => {
        dispatch(setEditLevantamentoFormNomeState(Nome))
    }, [dispatch])

    const setEditLevantamentoFormColunas = useCallback((Colunas: string[]) => {
        dispatch(setEditLevantamentoFormColunasState(Colunas))
    }, [dispatch])

    const setEditLevantamentoFormOpen = useCallback((open: boolean) => {
        dispatch(setEditLevantamentoFormOpenState(open))
    }, [dispatch])

    const processarLevantamento = useCallback(async (IdVelesGeoLevantamentoQuantitativo: number) => {
        if (currentUser) {
            try {
                const IdVelesGeoProcesso = await processarLevantamentoApi({
                    IdUsuario: currentUser.IdUsuario,
                    IdVelesGeoLevantamentoQuantitativo
                })

                return IdVelesGeoProcesso
            } catch (error: any) {
                setAlert({
                    message: error.response.data.error || error.response.data.message,
                    type: 'error'
                })
            }
        }
    }, [currentUser, processarLevantamentoApi, setAlert])

    const resetCreateLevantamentoForm = useCallback(() => {
        dispatch(resetCreateLevantamentoFormState())
    }, [dispatch])

    const resetEditLevantamentoForm = useCallback(() => {
        dispatch(resetEditLevantamentoFormState())
    }, [dispatch])

    const setOpenDownloadLevantamento = useCallback((open: boolean) => {
        dispatch(setOpenDownloadLevantamentoState(open))
    }, [dispatch])

    const downloadRelatorioQuantitativo = useCallback(() => {
        if (!extensaoArquivoSelecionada) {
            setAlert({
                message: 'Selecione uma extensão para o arquivo',
                type: 'warning'
            })
        } else {
            if (levantamentoSelecionado) {
                if (extensaoArquivoSelecionada.value === 'csv' || extensaoArquivoSelecionada.value === 'txt') {
                    let csv = convertToCSV(levantamentoSelecionado.Linhas.map((linha) => {
                        let orderedRow: any = {}
                        levantamentoSelecionado.Colunas.forEach((column: any) => {
                            orderedRow[column] = linha[column]
                        })

                        orderedRow['Total'] = linha['count']

                        return orderedRow
                    }))

                    let csvLines = csv.split('\n')
                    /* Change CSV Header */
                    csvLines[0] = levantamentoSelecionado.Colunas.map((column: any) => {
                        return columnValueToLabel(column as keyof IPessoaFisica)
                    }).join(',') + ',Total'

                    csv = csvLines.join('\n')

                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.setAttribute('href', url);
                    if (extensaoArquivoSelecionada.value === 'csv') {
                        link.setAttribute('download', levantamentoSelecionado.Nome + '.csv');
                    }
                    if (extensaoArquivoSelecionada.value === 'txt') {
                        link.setAttribute('download', levantamentoSelecionado.Nome + '.txt');
                    }
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                } else {
                    const transformedData = levantamentoSelecionado.Linhas.map((linha) => {
                        let orderedRow: any = {}

                        for (let i = 0; i < levantamentoSelecionado.Colunas.length; i++) {
                            const column = levantamentoSelecionado.Colunas[i]

                            orderedRow[String(columnValueToLabel(column as keyof IPessoaFisica))] = linha[column]
                        }

                        orderedRow['Total'] = linha['count']

                        return Object.values(orderedRow)
                    })

                    const lastRow = transformedData[transformedData.length - 1]
                    
                    if (lastRow && lastRow.length === levantamentoSelecionado.Colunas.length + 1) {
                        xlsxGenerator(
                            transformedData,
                            [...levantamentoSelecionado.Colunas.map((col) => String(columnValueToLabel(col as keyof IPessoaFisica))), 'Total'],
                            levantamentoSelecionado.Nome
                        )
                    } else {
                        // Remover a última linha do array
                        transformedData.pop()

                        // Passar os dados transformados para a função xlsxGenerator
                        xlsxGenerator(
                            transformedData,
                            [...levantamentoSelecionado.Colunas.map((col) => String(columnValueToLabel(col as keyof IPessoaFisica))), 'Total'],
                            levantamentoSelecionado.Nome
                        )
                    }
                }
            }

            dispatch(resetExtensaoArquivoSelecionadoState())
            dispatch(setOpenDownloadLevantamentoState(false))
            dispatch(resetLevantamentoSelecionadoState())
        }
    }, [extensaoArquivoSelecionada, setAlert, levantamentoSelecionado, dispatch])

    const setLevantamentoSelecionado = useCallback((levantamento: ILevantamentoQuantitativo) => {
        dispatch(setLevantamentoSelecionadoState(levantamento))
    }, [dispatch])

    const resetLevantamentoSelecionado = useCallback(() => {
        dispatch(resetLevantamentoSelecionadoState())
    }, [dispatch])

    const setOpenDeleteLevantamento = useCallback((open: boolean) => {
        dispatch(setOpenDeleteLevantamentoState(open))
    }, [dispatch])

    return {
        createLevantamento,
        updateLevantamento,
        deleteLevantamento,
        findLevantamentosByIdProjeto,
        setExtensaoArquivoSelecionado,
        resetExtensaoArquivoSelecionado,
        setCreateLevantamentoFormNome,
        setCreateLevantamentoFormColunas,
        setCreateLevantamentoFormOpen,
        setEditLevantamentoFormIdVelesGeoLevantamentoQuantitativo,
        setEditLevantamentoFormNome,
        setEditLevantamentoFormColunas,
        setEditLevantamentoFormOpen,
        processarLevantamento,
        resetCreateLevantamentoForm,
        resetEditLevantamentoForm,
        setOpenDownloadLevantamento,
        downloadRelatorioQuantitativo,
        setLevantamentoSelecionado,
        resetLevantamentoSelecionado,
        setOpenDeleteLevantamento
    }
}